import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Input, Tooltip } from 'antd';
import {
  GlobalOutlined, MailOutlined, PhoneOutlined, SendOutlined, IdcardOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import styles from './styles/text-input.module.css';
import sharedStyles from './styles/shared-components.module.css';
import { addAnswer } from '@/store/slices/chatbotFlow';
import { validate } from '@/helpers/Validators';
import { CandidateDataTypes, JobDataTypes } from '@/constants/DataTypes';

const TextInput = ({ block, addAnswer, isChatbotVisible, animTransition }) => {
  const { t } = useTranslation();

  const [input, setInput] = useState('');
  const [showError, setShowError] = useState(false);
  const [enterPressed, setEnterPressed] = useState(false); // use for prevent enter key press
  const [errorMessage, setErrorMessage] = useState('');

  const getErrorMessage = (block) => {
    if (typeof block.dataType === 'undefined') return '';
    switch (block.dataType?.toUpperCase()) {
      case JobDataTypes.CLIENT_EMAIL:
      case CandidateDataTypes.EMAIL:
        return t('textInputErrors.email');
      case JobDataTypes.CLIENT_PHONE:
      case CandidateDataTypes.PHONE:
      case CandidateDataTypes.VINCERE_EMERGENCY_PHONE:
        return t('textInputErrors.phone');
      case CandidateDataTypes.CITY:
      case JobDataTypes.CITY:
        return t('textInputErrors.city');
      case CandidateDataTypes.STATE:
      case JobDataTypes.STATE:
        return t('textInputErrors.state');
      case CandidateDataTypes.COUNTRY:
      case JobDataTypes.COUNTRY:
        return t('textInputErrors.country');
      case JobDataTypes.REQUIRED_YEARS:
        return t('textInputErrors.invalidNumber');
      case JobDataTypes.URL:
        return t('textInputErrors.url');
      case CandidateDataTypes.CANDIDATE_IELTS_SCORE:
        return t('textInputErrors.score');
      case CandidateDataTypes.FULL_NAME:
        return t('textInputErrors.fullName');
      case CandidateDataTypes.VISA_NUMBER:
        return t('textInputErrors.visaNumber');
      default:
        return t('textInputErrors.invalid');
    }
  };

  const submitInput = () => {

    if (validate(block, input)) {
      addAnswer(typeof input === 'string' ? input.trim() : input);
      setInput('');
      setShowError(false);
      setErrorMessage('');
    } else {
      setShowError(true);
      setErrorMessage(getErrorMessage(block));
    }
  };

  const getPlaceholder = () => {
    const countryCodeMandatory = block.options?.country_code_mandatory;

    switch (block.dataType?.toUpperCase()) {
      case CandidateDataTypes.VISA_NOTE:
        return 'Renewal required every year';
      case CandidateDataTypes.VISA_NUMBER:
        return '1234567890';
      case CandidateDataTypes.VISA_RENEWAL_DATE:
        return '2023-12-31';
      case CandidateDataTypes.VISA_STATUS:
        return 'Active';
      case CandidateDataTypes.VISA_TYPE:
        return 'Tier 2';
      case CandidateDataTypes.LINKEDIN:
        return 'www.linkedin.com/in/example';
      case JobDataTypes.CLIENT_EMAIL:
      case CandidateDataTypes.EMAIL:
        return 'john@doe.com';
      case JobDataTypes.CLIENT_PHONE:
      case CandidateDataTypes.PHONE:
      case CandidateDataTypes.VINCERE_EMERGENCY_PHONE:
        return countryCodeMandatory ? '+441234567890' : '05362257413';
      case JobDataTypes.REQUIRED_YEARS:
        return '3';
      case JobDataTypes.URL:
        return 'www.example.com';
      default:
        return t('defaultMessagePlaceholder');
    }
  };

  const getPrefixIcon = () => {
    const style = {
      color: 'rgba(0, 0, 0, 0.25)',
      marginRight: '4px',
    };
    switch (block.dataType?.toUpperCase()) {
      case CandidateDataTypes.VISA_NOTE:
      case CandidateDataTypes.VISA_NUMBER:
      case CandidateDataTypes.VISA_RENEWAL_DATE:
      case CandidateDataTypes.VISA_STATUS:
      case CandidateDataTypes.VISA_TYPE:
        return <IdcardOutlined style={style} />;
      case JobDataTypes.CLIENT_EMAIL:
      case CandidateDataTypes.EMAIL:
        return <MailOutlined style={style} />;
      case JobDataTypes.CLIENT_PHONE:
      case CandidateDataTypes.PHONE:
      case CandidateDataTypes.VINCERE_EMERGENCY_PHONE:
        return <PhoneOutlined style={style} />;
      case JobDataTypes.URL:
        return <GlobalOutlined style={style} />;
      default:
        return null;
    }
  };

  return (
    <div className={sharedStyles.wrapper}>
      <div className={styles.input_container}>
        <Input.TextArea
          className={styles.input}
          value={input}
          autoFocus
          placeholder={getPlaceholder()}
          prefix={getPrefixIcon()}
          autoSize={{
            minRows: 1, maxRows: 6,
          }}
          onChange={(e) => {
            if (enterPressed) { // prevent enter key press, it will go to new line which is not desired
              setEnterPressed(false);
              return;
            }

            setInput(e.target.value);
            if (showError) { // clear error message when user start typing
              setShowError(false);
              setErrorMessage('');
            }
          }}
          variant={'borderless'}
          onPressEnter={() => {
            setEnterPressed(true);
            submitInput();
          }}
        />
      </div>
      <Tooltip
        placement="topRight"
        title={errorMessage}
        align={{ offset: [-5, -5] }}
        open={showError && isChatbotVisible && animTransition}
      >
        <Button
          className={sharedStyles.send_button}
          type="text"
          danger={showError}
          onClick={submitInput}
          icon={<SendOutlined style={{ fontSize: '24px' }} />}
        />
      </Tooltip>
    </div>
  );
};

TextInput.propTypes = {
  block: PropTypes.shape({
    dataType: PropTypes.string.isRequired,
    options: PropTypes.shape({
      country_code_mandatory: PropTypes.bool,
    }),
  }).isRequired,
  addAnswer: PropTypes.func.isRequired,
  isChatbotVisible: PropTypes.bool.isRequired,
  animTransition: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  isChatbotVisible: state.chatbotVisibility.isVisible,
  animTransition: state.chatbotVisibility.animTransition,
});

const mapDispatchToProps = { addAnswer };

export default connect(mapStateToProps, mapDispatchToProps)(TextInput);
